import { useEffect, useMemo, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ServiceSearch } from "../../../../../@types/service-search";
import { useDebouncedValue } from "../../../../../hooks/useDebounce";
import { Totem } from "../../../../../@types/totem";
import { useAttendanceLocalServiceList } from "../../../../../services/api-tansquery/attendance-local/service/list";
import { AttendanceLocal } from "../../../../../@types/attendance-local";

type Services = Array<{
    id: number;
    slug: string;
    titulo: string;
}>;

type Props = {
    field: ControllerRenderProps<{
        descricao?: string;
        cor_fonte?: string;
        cor_fundo?: string;
        local_atendimento?: string;
        orgao?: string;
        servicos?: string[];
        prioridades?: string[];
        logo?: File | string;
    }, "servicos">
    fieldState: ControllerFieldState;
    unidade_slug: AttendanceLocal["slug_unidade"];
    totem?: Totem;
    style?: React.CSSProperties;
}

export default function ServicesField({ field, fieldState, style, unidade_slug, totem }: Props) {
    const [search, setSearch] = useState<string>(undefined);
    const [previousSelected, setPreviousSelected] = useState<Services>([]);
    const debouncedSearch = useDebouncedValue(search, 500);
    const { data, isLoading } = useAttendanceLocalServiceList({
        unidade_slug,
        page: 1, search: debouncedSearch,
    });

    const serviceList = useMemo(() => {
        return data?.results || [];
    }, [data]);

    const options = useMemo(() => {
        const servList = serviceList.map(item => ({
            id: item.servico.id,
            slug: item.unidade,
            titulo: item.servico.titulo,
        }));
        const merged = [...previousSelected, ...servList];
        return merged.filter(
            (item, index, self) => self.findIndex(
                i => i.id === item.id
            ) === index
        );
    }, [serviceList, previousSelected]);

    function handleInputChange(_, value: string, reason: string) {
        if (reason === "input") {
            setSearch(value === "" ? undefined : value);
        }
    }

    function handleChange(_, newValue: ServiceSearch[]) {
        setSearch(undefined);
        setPreviousSelected(newValue);
        field.onChange(newValue.map(item => item.id.toString()));
    }

    useEffect(() => {
        if (totem?.servicos) {
            setPreviousSelected(totem.servicos);
        }
    }, [totem?.servicos]);

    useEffect(() => {
        if (totem) {
            if (totem.local_atendimento.slug !== unidade_slug || unidade_slug === "") {
                field.onChange([]);
                setPreviousSelected([]);
            } else {
                setPreviousSelected(totem?.servicos || []);
                field.onChange(totem?.servicos?.map(item => item.id.toString()) || []);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unidade_slug, totem]);

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            style={style}
            freeSolo={true}
            options={options}
            value={options.filter((item) => field.value.includes(item.id.toString())) || []}
            inputValue={search || ""}
            onChange={handleChange}
            onInputChange={handleInputChange}
            getOptionLabel={(option) => option.titulo}
            getOptionSelected={(option, value) => option.id === value.id}
            loading={isLoading}
            loadingText="Carregando serviços..."
            noOptionsText="Nenhum serviço encontrado"
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label="Serviços"
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || "Selecione os serviços oferecidos no totem"}
                    InputProps={{
                        ...params.InputProps,
                        required: false,
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
